import { toMs } from "@/lib/timestamp";

interface GroupableSegment {
  id: string;
  start: string;
  end: string;
  serviceAddonId?: string | null;
  service?: { name?: string } | null;
}

export interface ServiceAddonGroup<T extends GroupableSegment> {
  parent: T;
  addons: T[];
  start: string;
  end: string;
}

/**
 * Groups add-on segments under the service they belong to: the latest
 * non-add-on segment starting at or before them. When `isParentOf` is given,
 * only a segment it accepts can hold the add-on, so a concurrent service that
 * doesn't offer it can't claim it. An add-on with no parent stands alone.
 */
export function groupServiceAddons<T extends GroupableSegment>(
  segments: T[],
  isParentOf?: (parent: T, addon: T) => boolean,
): ServiceAddonGroup<T>[] {
  // On a start tie the parent goes first, so a concurrent add-on that ends
  // sooner still finds it.
  const sorted = [...segments].sort(
    (a, b) =>
      toMs(a.start) - toMs(b.start) ||
      Number(!!a.serviceAddonId) - Number(!!b.serviceAddonId) ||
      toMs(a.end) - toMs(b.end),
  );
  const groups: ServiceAddonGroup<T>[] = [];
  for (const segment of sorted) {
    const parentGroup = segment.serviceAddonId
      ? [...groups]
          .reverse()
          .find(
            (group) =>
              !group.parent.serviceAddonId &&
              toMs(group.parent.start) <= toMs(segment.start) &&
              (!isParentOf || isParentOf(group.parent, segment)),
          )
      : undefined;
    if (!parentGroup) {
      groups.push({
        parent: segment,
        addons: [],
        start: segment.start,
        end: segment.end,
      });
      continue;
    }
    parentGroup.addons.push(segment);
    if (toMs(segment.end) > toMs(parentGroup.end)) {
      parentGroup.end = segment.end;
    }
  }
  return groups;
}

/**
 * An `isParentOf` for `groupServiceAddons` from the service catalog: a service
 * holds an add-on only if its catalog entry lists it. Undefined until the
 * catalog loads, so grouping falls back to timing alone.
 */
export function catalogAddonParent<T extends GroupableSegment>(
  services: { id: string; addons?: { id: string }[] | null }[],
  serviceIdOf: (segment: T) => string | undefined,
): ((parent: T, addon: T) => boolean) | undefined {
  if (services.length === 0) return undefined;
  const addonIdsByServiceId = new Map(
    services.map((svc) => [
      svc.id,
      new Set(svc.addons?.map((addon) => addon.id) ?? []),
    ]),
  );
  return (parent, addon) =>
    !!addon.serviceAddonId &&
    !!addonIdsByServiceId
      .get(serviceIdOf(parent) ?? "")
      ?.has(addon.serviceAddonId);
}

/**
 * Whether `parent` is the service `addon` was booked under. An add-on that
 * doesn't name its parent can sit under any service.
 */
export function isAddonParent(
  parent: { service?: { id?: string } | null },
  addon: { addonParentServiceId?: string | null },
): boolean {
  return (
    !addon.addonParentServiceId ||
    addon.addonParentServiceId === parent.service?.id
  );
}

/** "BBL: Face" under "BBL" reads as "Face". */
export function addonDisplayName(
  addonName: string | undefined,
  parentName: string | undefined,
): string {
  if (!addonName) return "";
  if (!parentName) return addonName;
  for (const separator of [":", " -", " –"]) {
    const prefix = `${parentName}${separator}`;
    if (addonName.startsWith(prefix) && addonName.length > prefix.length) {
      return addonName.slice(prefix.length).trim();
    }
  }
  return addonName;
}
